import React from 'react';

/**
 * Zeigt den Kern von Dartware Connect: viele Lieferanten, ein Anschluss in der Mitte,
 * der Shop auf der anderen Seite. Reine HTML/SVG-Grafik, ohne Bilder.
 * Ab "sm" nebeneinander, darunter gestapelt.
 */
const suppliers = ['Marke A', 'Marke B', 'Großhändler', 'Großhändler', 'Marke C'];
const shops = ['Shopify', 'WooCommerce', 'CS-Cart', 'Eigener Shop'];

const Node: React.FC<{ label: string; muted?: boolean }> = ({ label, muted }) => (
  <div className={`whitespace-nowrap rounded-xl border px-2.5 py-1.5 text-[12px] ${muted ? 'border-white/[0.08] bg-white/[0.03] text-fg-2' : 'border-white/10 bg-surface-3 text-fg'}`}>{label}</div>
);

const HLine: React.FC<{ flip?: boolean }> = ({ flip }) => (
  <svg viewBox="0 0 80 12" className={`h-3 w-full ${flip ? 'rotate-180' : ''}`} preserveAspectRatio="none" aria-hidden>
    <line x1="0" y1="6" x2="80" y2="6" stroke="rgba(255,255,255,0.12)" strokeWidth="1.5" />
    <line x1="0" y1="6" x2="80" y2="6" stroke="#3fced1" strokeWidth="1.5" strokeDasharray="6 6" className="animate-dash" />
  </svg>
);

const VLine: React.FC = () => (
  <svg viewBox="0 0 12 40" className="mx-auto h-8 w-3" preserveAspectRatio="none" aria-hidden>
    <line x1="6" y1="0" x2="6" y2="40" stroke="rgba(255,255,255,0.12)" strokeWidth="1.5" />
    <line x1="6" y1="0" x2="6" y2="40" stroke="#3fced1" strokeWidth="1.5" strokeDasharray="6 6" className="animate-dash" />
  </svg>
);

const Center: React.FC = () => (
  <div className="flex flex-col items-center gap-2">
    <div className="rounded-2xl border border-accent/40 bg-accent/10 px-4 py-3 text-center shadow-glow sm:px-5 sm:py-4">
      <div className="text-[10px] font-medium uppercase tracking-[0.14em] text-accent">Dartware</div>
      <div className="text-lg font-semibold tracking-tight text-fg">Connect</div>
    </div>
    <div className="text-center text-[11px] leading-relaxed text-fg-3">Bestand · Preise · Daten<br />Bestellungen · Tracking</div>
  </div>
);

const Label: React.FC<{ children: React.ReactNode; align?: 'left' | 'right' | 'center' }> = ({ children, align = 'left' }) => (
  <p className={`mb-2 text-[10px] font-medium uppercase tracking-[0.14em] text-fg-3 text-${align}`}>{children}</p>
);

const ConnectDiagram: React.FC = () => (
  <div className="relative overflow-hidden rounded-3xl border border-line bg-[#0b0b0d] p-5 sm:p-6">
    <div aria-hidden className="pointer-events-none absolute inset-0 opacity-[0.15]" style={{ background: 'radial-gradient(50% 50% at 50% 50%, #3fced1, transparent 70%)' }} />

    {/* Mobil: gestapelt */}
    <div className="relative sm:hidden">
      <Label align="center">Lieferanten</Label>
      <div className="flex flex-wrap justify-center gap-2">{suppliers.map((s, i) => <Node key={i} label={s} muted />)}</div>
      <VLine />
      <Center />
      <VLine />
      <div className="flex flex-wrap justify-center gap-2">{shops.map((s, i) => <Node key={i} label={s} />)}</div>
      <p className="mt-2 text-center text-[10px] font-medium uppercase tracking-[0.14em] text-fg-3">Dein Shop</p>
    </div>

    {/* Ab sm: nebeneinander */}
    <div className="relative hidden grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] items-center gap-3 sm:grid">
      <div>
        <Label>Lieferanten</Label>
        <div className="space-y-2">
          {suppliers.map((s, i) => (
            <div key={i} className="flex items-center gap-1.5"><Node label={s} muted /><div className="min-w-[12px] flex-1"><HLine /></div></div>
          ))}
        </div>
      </div>
      <Center />
      <div>
        <Label align="right">Dein Shop</Label>
        <div className="space-y-2">
          {shops.map((s, i) => (
            <div key={i} className="flex items-center justify-end gap-1.5"><div className="min-w-[12px] flex-1"><HLine flip /></div><Node label={s} /></div>
          ))}
        </div>
      </div>
    </div>
  </div>
);

export default ConnectDiagram;
